import { PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, DatePicker, Flex, Form, Input, message, Table, Tag, Tooltip } from "antd";
import dayjs from "dayjs";
import { useBoolean } from "hooks/use-boolean";
import { get } from "lodash";
import { useSearchParams } from "react-router-dom";
import axiosInstance, { endpoints } from "services/axios";

const ReDeadlinePage = () => {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const accessToken = searchParams.get("token");
  const taskId = searchParams.get("taskId");
  const taskFormBool = useBoolean();

  const [form] = Form.useForm();

  const { data, isFetching } = useQuery({
    queryKey: ["task-re-deadline-list", taskId],
    queryFn: async () => {
      axiosInstance.defaults.headers.common.Authorization = accessToken;
      return await axiosInstance.get(endpoints.taskReDeadline.get(taskId as string));
    },
  });

  const { mutate: createReDeadline, isPending } = useMutation({
    mutationFn: async (data: any) => await axiosInstance.post(endpoints.taskReDeadline.create, data),
    onSuccess: () => {
      message.success("Successfully created!");
      queryClient.invalidateQueries({ queryKey: ["task-re-deadline-list", taskId] });
      cancel();
    },
  });

  const onFinish = (data: any) => {
    const [hours, minutes] = data.time.split(":");
    const combinedDateTime = dayjs(data.date).set("hour", hours).set("minute", minutes);
    data.newDeadline = combinedDateTime.valueOf();
    delete data.date;
    delete data.time;
    createReDeadline(data);
  };

  const cancel = () => {
    taskFormBool.onFalse();
    form.resetFields();
  };

  const columns = [
    {
      title: "№",
      dataIndex: "no",
      render: (_: any, i: any, index: number) => index + 1,
      width: 50,
    },
    {
      title: "Yaratilgan vaqti",
      dataIndex: "createdAt",
      key: "createdAt",
      onCell: () => ({
        className: "!p-0",
      }),
      render: (value: any, record: any) => (
        <Tooltip className="flex justify-start items-center h-[50px] w-full" title={record.comment}>
          {dayjs(value).format("DD.MM.YYYY HH:mm")}
        </Tooltip>
      ),
    },
    {
      title: "Eski muddat",
      dataIndex: "oldDeadline",
      key: "oldDeadline",
      render: (value: any) => (value ? dayjs(value).format("DD.MM.YYYY HH:mm") : "-"),
    },
    {
      title: "Yangi muddat",
      dataIndex: "newDeadline",
      key: "newDeadline",
      render: (value: any) => (value ? dayjs(value).format("DD.MM.YYYY HH:mm") : "-"),
    },
    {
      title: "Ko'rib chiqilgan vaqt",
      dataIndex: "confirmedTimestamp",
      key: "confirmedTimestamp",
      onCell: () => ({
        className: "!p-0",
      }),
      render: (value: any, record: any) => (
        <Tooltip placement="topLeft" className="flex justify-start items-center h-[50px]" title={record?.rejectReason}>
          {value ? dayjs(value).format("DD.MM.YYYY HH:mm") : "-"}
        </Tooltip>
      ),
    },
    {
      title: "Holati",
      dataIndex: "status",
      key: "status",
      render: (value: any) => <Tag color={value === "PENDING" ? "orange" : value === "REJECTED" ? "red" : "green"}>{value}</Tag>,
    },
  ];

  return (
    <div className="pt-4">
      <Table loading={isFetching} rowKey={"id"} pagination={false} dataSource={get(data, "data.data", [])} columns={columns} />

      <Flex className="mt-3">
        {!taskFormBool.value ? (
          <Button onClick={taskFormBool.onTrue} type="primary" icon={<PlusOutlined />}>
            So'rov yuborish
          </Button>
        ) : (
          <Card className="w-full">
            <Form
              className="w-full"
              onFinish={onFinish}
              form={form}
              initialValues={{
                taskId,
                time: "20:00",
              }}
            >
              <Flex gap={20}>
                <Form.Item name="date" label={<span className="font-bold">Muddat</span>} rules={[{ required: true, message: "Sana kiritish majburiy!" }]}>
                  <DatePicker needConfirm={false} />
                </Form.Item>
                <Form.Item name="time" rules={[{ required: true, message: "Vaqt kiritish majburiy!" }]}>
                  <Input type="time" />
                </Form.Item>
              </Flex>

              <Form.Item label={<span className="font-bold">Sabab</span>} name="comment" rules={[{ required: true, message: "Sabab kiritish majburiy!" }]}>
                <Input.TextArea autoSize={{ minRows: 3 }} placeholder="Sabab..." />
              </Form.Item>

              <Form.Item name="taskId" hidden>
                <Input />
              </Form.Item>

              <Flex justify="flex-start" align="center" gap="10px">
                <Button onClick={cancel}>Bekor qilish</Button>
                <Button loading={isPending} type="primary" htmlType="submit">
                  Yuborish
                </Button>
              </Flex>
            </Form>
          </Card>
        )}
      </Flex>
    </div>
  );
};
export default ReDeadlinePage;
